// @wc-ignore-file
// keep in sync with backend/api-server/src/lib/figma/figma-url.ts (the server
// re-parses every link; this copy only decides where a card is shown).
// `shared/` is types-only at runtime — see CLAUDE.md. The strings here are
// data (hosts, URL parts), so wuchale must not extract them.

/**
 * CR-126: the one parser deciding which pasted links are Figma targets.
 *
 * Accepted: `https://www.figma.com/design/:key/:name?node-id=…` (current),
 * `/file/:key/…` (legacy) and `/proto/:key/…` (imported as the file/frame it
 * points to). A `node-id` makes the target a frame (any node really — a page
 * link is a frame link too); none makes it the whole file.
 *
 * Rejected: other hosts (figma.com subdomains included), FigJam `/board/`,
 * `/slides/`, branch URLs (`/design/:key/branch/:branchKey/…`), malformed
 * keys or node ids, credentials in the URL.
 */

export type FigmaTargetType = "file" | "frame";

export interface FigmaTarget {
  fileKey: string;
  /** API form (`1:2`), or null for a whole-file target. */
  nodeId: string | null;
  targetType: FigmaTargetType;
  /** `https://www.figma.com/design/:key[/:name][?node-id=1-2]` — tracking params dropped. */
  canonicalUrl: string;
}

/** The longest URL considered at all. */
export const MAX_FIGMA_URL_LENGTH = 2048;

const HOSTS = new Set(["www.figma.com", "figma.com"]);
const KINDS = new Set(["design", "file", "proto"]);
const FILE_KEY = /^[A-Za-z0-9]{1,128}$/;
/** URL form of a node id: `1-2`, `1:2`, instance ids `I1-2;3-4`. */
const NODE_ID = /^[A-Za-z0-9:;-]{1,255}$/;
/** A file-name slug worth keeping in the canonical URL. */
const NAME_SLUG = /^[A-Za-z0-9._~%-]{1,200}$/;

/** Parse a pasted link; null when it is not a supported Figma target. */
export function parseFigmaUrl(raw: string): FigmaTarget | null {
  if (typeof raw !== "string") return null;
  const text = raw.trim();
  if (text.length === 0 || text.length > MAX_FIGMA_URL_LENGTH) return null;

  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (url.username || url.password || url.port) return null;
  if (!HOSTS.has(url.hostname.toLowerCase())) return null;

  const segments = url.pathname.split("/").filter((s) => s.length > 0);
  const [kind, fileKey, ...rest] = segments;
  if (!kind || !KINDS.has(kind) || !fileKey || !FILE_KEY.test(fileKey)) return null;
  // Branch links point at a branch file the importer does not model in V1.
  if (rest[0] === "branch") return null;

  const rawNodeId = url.searchParams.get("node-id");
  let nodeId: string | null = null;
  if (rawNodeId !== null) {
    const trimmed = rawNodeId.trim();
    if (!NODE_ID.test(trimmed)) return null;
    nodeId = trimmed.replace(/-/g, ":");
  }

  const name = rest[0] && NAME_SLUG.test(rest[0]) ? `/${rest[0]}` : "";
  const query = nodeId ? `?node-id=${encodeURIComponent(nodeId.replace(/:/g, "-"))}` : "";
  return {
    fileKey,
    nodeId,
    targetType: nodeId ? "frame" : "file",
    canonicalUrl: `https://www.figma.com/design/${fileKey}${name}${query}`,
  };
}

/** A Figma target found in a message, with the link as it was written. */
export interface FigmaLinkTarget extends FigmaTarget {
  sourceUrl: string;
}

/** At most this many cards under one message. */
export const MAX_FIGMA_TARGETS_PER_MESSAGE = 5;

/** Candidate links: stops at whitespace and markdown/HTML delimiters. */
const LINK = /https?:\/\/(?:www\.)?figma\.com\/[^\s<>()[\]"'`]+/gi;
/** Sentence punctuation a link rarely ends with. */
const TRAILING = /[.,;:!?*_~]+$/;

/** The identity of a target in a project (D1: one import per file + node). */
export function figmaTargetKey(target: Pick<FigmaTarget, "fileKey" | "nodeId">): string {
  return `${target.fileKey}:${target.nodeId ?? ""}`;
}

/**
 * The distinct supported Figma targets linked from a message's text, in order
 * of first appearance (D7: one card per target, under the message).
 */
export function extractFigmaTargets(text: string): FigmaLinkTarget[] {
  if (!text) return [];
  const seen = new Set<string>();
  const targets: FigmaLinkTarget[] = [];
  for (const match of text.matchAll(LINK)) {
    const sourceUrl = match[0].replace(TRAILING, "");
    const target = parseFigmaUrl(sourceUrl);
    if (!target) continue;
    const key = figmaTargetKey(target);
    if (seen.has(key)) continue;
    seen.add(key);
    targets.push({ ...target, sourceUrl });
    if (targets.length === MAX_FIGMA_TARGETS_PER_MESSAGE) break;
  }
  return targets;
}
